import React, { useCallback, useState } from 'react';

import { Autocomplete, TextField, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, AlineChip, FilterTags } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { useGetUserLabels } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  createAutocompleteFilterOptions,
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
  useUniqueFilterOptions,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
  },
}));

// Use Partial b/c we have no guarantee of the object shape coming out of apiRef.getRowModels.
export type LabelFilterOption = Partial<Pick<Apollo.Label, 'id' | 'labelText' | '__typename'>>;

const filterOptions = createAutocompleteFilterOptions<LabelFilterOption>('labelText');

export function LabelAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const { labels, refetch } = useGetUserLabels();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<LabelFilterOption[]>(item.value ?? []);
  const compareFn = useCallback(
    (a: LabelFilterOption, b: LabelFilterOption) =>
      a.labelText?.localeCompare(b.labelText ?? '') ?? 0,
    []
  );
  const sortedOptions = useUniqueFilterOptions({ compareFn, items: labels });
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: LabelFilterOption[]): void {
    applyValue({
      ...item,
      value,
    });
    setValue(value);
  }

  return (
    <Autocomplete<LabelFilterOption, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      filterOptions={filterOptions}
      getOptionKey={option => option.id ?? ''}
      getOptionLabel={option => option.labelText ?? ''}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={sortedOptions}
      renderInput={params => {
        return <TextField placeholder="Search labels..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <AlineChip chipText={option.labelText ?? ''} />
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<LabelFilterOption>
            getTextFromTag={tag => tag.labelText ?? ''}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(({ id }) => tag.id !== id))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, value) => handleValueChange(value)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
      onOpen={async () => {
        await refetch();
      }}
    />
  );
}
